import { useCallback, useMemo, useState } from "react";
import { useSearchParams } from "react-router";
import type { FilterDef, FilterValues } from "./types";

type ClasseurState<T> = {
  items: T[];
  total: number;
  isLoading: boolean;
};

type UseClasseurFiltersOptions = {
  filters: FilterDef[];
};

export function useClasseurFilters({ filters }: UseClasseurFiltersOptions) {
  const [searchParams, setSearchParams] = useSearchParams();

  const activeFilters: FilterValues = useMemo(() => {
    const result: FilterValues = {};
    for (const f of filters) {
      const param = searchParams.get(f.key);
      result[f.key] = param ? param.split(",").filter(Boolean) : [];
    }
    return result;
  }, [filters, searchParams]);

  const onFilterChange = useCallback(
    (newFilters: FilterValues) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          for (const [key, values] of Object.entries(newFilters)) {
            if (values.length > 0) {
              next.set(key, values.join(","));
            } else {
              next.delete(key);
            }
          }
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  return { activeFilters, onFilterChange };
}

export function useClasseurState<T>(initial?: { items: T[]; total: number }) {
  return useState<ClasseurState<T>>({
    items: initial?.items ?? [],
    total: initial?.total ?? 0,
    isLoading: true,
  });
}
